import { Alert, Button } from "~/components/ui";
import { CheckCircle, Loader2 } from "lucide-react";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import type { ActionCardStatus } from "@noa/api-core";

interface ActionCardStatusDisplayProps {
  status: ActionCardStatus;
  error?: string;
  onRetry?: () => void;
  /**
   * Namespace-qualified i18n key for the success line (e.g.
   * `"lexicon:actionCard.added"`). Actions that really persist something set
   * this; those that only simulate a submit leave it unset and keep the
   * prototype notice below.
   */
  successMessageKey?: string;
  /** Interpolated into the success line when the action names a term. */
  term?: string;
}

export const ActionCardStatusDisplay: FunctionComponent<ActionCardStatusDisplayProps> = ({
  status,
  error,
  onRetry,
  successMessageKey,
  term,
}) => {
  // No namespace bound here: this component renders every action type, so
  // `successMessageKey` carries its own (`"lexicon:actionCard.added"`). Binding
  // one action's namespace would make any other action's key resolve to nothing
  // and render the raw key string as the user's success confirmation.
  const { t } = useTranslation();
  if (status === "submitting") {
    return (
      <div className="action-card-status action-card-status--loading" data-testid="action-card-submitting">
        <Loader2 size={16} className="animate-spin" />
        <span>Envoi en cours...</span>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="action-card-status action-card-status--success" data-testid="action-card-success">
        <CheckCircle size={16} />
        <span>
          {successMessageKey
            ? t(successMessageKey, { term })
            : "[PROTOTYPE] Demande simulée — intégration réelle à venir"}
        </span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div data-testid="action-card-error">
        <Alert
          type="error"
          message={error || "Une erreur est survenue"}
          className="action-card-alert"
          action={
            onRetry && (
              <Button size="small" onClick={onRetry} data-testid="action-card-retry">
                Réessayer
              </Button>
            )
          }
        />
      </div>
    );
  }

  return null;
};
